import { HttpFetch } from "utils/hx/service/http";

import { Paginated, Unit } from "adl-gen/common";
import * as AR from "adl-gen/adminui/api";
import { MetaAdlDecl } from "adl-gen/adminui/db";
import { DbKey, WithDbId } from "adl-gen/common/db";
import { TableQuery } from "adl-gen/common/tabular";
import { DeclResolver } from "adl-gen/runtime/adl";
import { AdminUiHttpServiceBase } from "./admin-ui-http-service-base";
import { HttpServiceError } from "../http-service-error";
import { PostFn } from "../types";

import { AdminUiService } from "./admin-ui-service";
import { ErrorOnColumn, rowValidation } from "./admin-ui-validation-hooks";
import { PartialUpdateReq } from "adl-gen/adminui/api";

/**
 * The adminUI HttpService
 */
export class AdminUiHttpService
  extends AdminUiHttpServiceBase
  implements AdminUiService
{
  postQueryTables: PostFn<TableQuery, Paginated<AR.Table>>;
  postQueryDecls: PostFn<TableQuery, Paginated<MetaAdlDecl>>;
  postCreate: PostFn<AR.CreateReq, AR.DbResult<DbKey<AR.DbRow>>>;
  postQuery: PostFn<AR.QueryReq, Paginated<WithDbId<AR.DbRow>>>;
  postQueryWithoutIds: PostFn<AR.QueryReq, Paginated<AR.DbRow>>;
  postUpdate: PostFn<AR.UpdateReq, AR.DbResult<Unit>>;
  postPartialUpdate: PostFn<PartialUpdateReq, AR.DbResult<Unit>>;
  postDelete: PostFn<AR.DeleteReq, AR.DbResult<Unit>>;
  postDbKeyLabels: PostFn<AR.DbKeyLabelReq[], AR.DbKeyLabelResp[]>;

  constructor(
    /** Fetcher over HTTP */
    http: HttpFetch,
    /** Base URL of the API endpoints */
    baseUrl: string,
    /** Resolver for ADL types */
    resolver: DeclResolver,
    /** The authentication token (if any) */
    authToken: string | undefined,
    /** Error handler to allow for cross cutting concerns, e.g. authorization errors */
    handleError: (error: HttpServiceError) => void,
    /** Optional API domain parameter Eg. api.dev.ferovinum.link */
    domain?: string,
  ) {
    super(http, baseUrl, resolver, authToken, handleError, domain);

    const adminApi = this.annotatedApi(
      AR.snAdminApiRequests,
      AR.makeAdminApiRequests({}),
    );
    this.postQueryTables = this.mkPostFn(adminApi.queryTables);
    this.postQueryDecls = this.mkPostFn(adminApi.queryDecls);
    this.postCreate = this.mkPostFn(adminApi.create);
    this.postQuery = this.mkPostFn(adminApi.query);
    this.postQueryWithoutIds = this.mkPostFn(adminApi.queryWithoutIds);
    this.postUpdate = this.mkPostFn(adminApi.update);
    this.postPartialUpdate = this.mkPostFn(adminApi.partialUpdate);
    this.postDelete = this.mkPostFn(adminApi.delete);
    this.postDbKeyLabels = this.mkPostFn(adminApi.dbKeyLabels);
  }

  async validate<T>(req: AR.CreateReq): Promise<ErrorOnColumn<T>> {
    return rowValidation<T>(req);
  }

  async queryTables(req: TableQuery): Promise<Paginated<AR.Table>> {
    return this.postQueryTables.call(req);
  }

  async queryDecls(req: TableQuery): Promise<Paginated<MetaAdlDecl>> {
    return this.postQueryDecls.call(req);
  }

  async create(req: AR.CreateReq): Promise<AR.DbResult<DbKey<AR.DbRow>>> {
    return this.postCreate.call(req);
  }

  async query(req: AR.QueryReq): Promise<Paginated<WithDbId<AR.DbRow>>> {
    return this.postQuery.call(req);
  }

  async queryWithoutIds(req: AR.QueryReq): Promise<Paginated<AR.DbRow>> {
    return this.postQueryWithoutIds.call(req);
  }

  async update(
    req: AR.UpdateReq,
    origianlValues?: AR.DbRow,
  ): Promise<AR.DbResult<Unit>> {
    if (origianlValues) {
      // use partial update if possible, avoids conflicts
      const updateReq: PartialUpdateReq = {
        ...req,
        values: { id: req.values.id, value: {} },
        updateColumns: [],
      };
      Object.getOwnPropertyNames(req.values.value).forEach((key) => {
        let o = origianlValues[key] as any;
        const n = req.values.value[key] as any;
        if (o !== n) {
          // cannot directly compare objects
          if (typeof o === "object") {
            if (o && n && typeof n === "object") {
              const nJson = JSON.stringify(n);
              if (nJson === JSON.stringify(o)) {
                return;
              }
            }
          }
          updateReq.updateColumns.push(key);
        }
      });
      if (updateReq.updateColumns.length) {
        updateReq.updateColumns.forEach((key) => {
          updateReq.values.value[key] = req.values.value[key];
        });
        return this.postPartialUpdate.call(updateReq);
      }
      return Promise.resolve({
        kind: "dbError",
        value: "No columns to update",
      });
    }

    return this.postUpdate.call(req);
  }

  async delete(req: AR.DeleteReq): Promise<AR.DbResult<Unit>> {
    return this.postDelete.call(req);
  }

  async dbKeyLabels(req: AR.DbKeyLabelReq[]): Promise<AR.DbKeyLabelResp[]> {
    return this.postDbKeyLabels.call(req);
  }
}
